import { THEME_PRESETS, LOYALTY_MODE_LABELS } from "@/theme";
import { Input } from "./ui/input";

// Reusable editor for branding + loyalty rules. `value` is a flat config object.
export const LoyaltyBrandingFields = ({ value, onChange, idPrefix = "cfg" }) => {
  const set = (patch) => onChange({ ...value, ...patch });
  const applyPreset = (key) => {
    const p = THEME_PRESETS[key];
    set({ theme_preset: key, primary_color: p.primary, secondary_color: p.secondary });
  };

  return (
    <div className="space-y-5">
      {/* Loyalty mode */}
      <div>
        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Treue-Modell</label>
        <div className="grid grid-cols-1 gap-2 mt-2">
          {Object.entries(LOYALTY_MODE_LABELS).map(([mode, label]) => (
            <button
              key={mode}
              type="button"
              data-testid={`${idPrefix}-mode-${mode}`}
              onClick={() => set({ mode })}
              className={`text-left rounded-2xl px-4 py-3 ring-1 press transition-colors ${
                value.mode === mode
                  ? "ring-[var(--bs-teal)] bg-teal-50/70"
                  : "ring-slate-200 bg-white hover:bg-slate-50"
              }`}
            >
              <p className="font-display font-bold text-slate-900 text-sm">{label}</p>
              <p className="text-[12px] text-slate-500 mt-0.5">
                {mode === "stamp_fixed" && "Ein Stempel pro Einkauf. Klassische Stempelkarte."}
                {mode === "stamp_spend" && "Stempel je nach Umsatz (z. B. 10 € = 1 Stempel)."}
                {mode === "points_spend" && "Punkte je nach Umsatz, Belohnung bei Ziel-Punktzahl."}
              </p>
            </button>
          ))}
        </div>
      </div>

      {/* Mode-specific params */}
      <div className="grid grid-cols-2 gap-3">
        {value.mode !== "points_spend" && (
          <Field label="Stempel pro Karte">
            <Input data-testid={`${idPrefix}-stamps-per-card`} type="number" min={2} max={50}
              value={value.stamps_per_card}
              onChange={(e) => set({ stamps_per_card: parseInt(e.target.value || "0", 10) })}
              className="h-11 rounded-xl" />
          </Field>
        )}
        {value.mode === "stamp_spend" && (
          <>
            <Field label={`Umsatz je Stempel (${value.currency})`}>
              <Input data-testid={`${idPrefix}-spend-per-stamp`} type="number" min={1} step="0.5"
                value={value.spend_per_stamp}
                onChange={(e) => set({ spend_per_stamp: parseFloat(e.target.value || "0") })}
                className="h-11 rounded-xl" />
            </Field>
            <Field label="Restbetrag übertragen" className="col-span-2">
              <button type="button" data-testid={`${idPrefix}-carry-toggle`}
                onClick={() => set({ carry_remainder: !value.carry_remainder })}
                className={`w-full h-11 rounded-xl ring-1 font-bold text-sm press ${
                  value.carry_remainder ? "bg-teal-50 ring-[var(--bs-teal)] text-[var(--bs-teal)]" : "bg-white ring-slate-200 text-slate-500"
                }`}>
                {value.carry_remainder ? "Ja — Rest wird angerechnet" : "Nein — Rest verfällt"}
              </button>
            </Field>
          </>
        )}
        {value.mode === "points_spend" && (
          <>
            <Field label="Punkte je Währungseinheit">
              <Input data-testid={`${idPrefix}-points-per-currency`} type="number" min={0.1} step="0.1"
                value={value.points_per_currency}
                onChange={(e) => set({ points_per_currency: parseFloat(e.target.value || "0") })}
                className="h-11 rounded-xl" />
            </Field>
            <Field label="Punkte für Belohnung">
              <Input data-testid={`${idPrefix}-reward-threshold`} type="number" min={1}
                value={value.reward_threshold}
                onChange={(e) => set({ reward_threshold: parseInt(e.target.value || "0", 10) })}
                className="h-11 rounded-xl" />
            </Field>
          </>
        )}
      </div>

      {value.mode === "stamp_fixed" && (
        <Field label="Stempel sammeln pro">
          <Input data-testid={`${idPrefix}-stamp-earning-unit`} value={value.stamp_earning_unit || ""}
            onChange={(e) => set({ stamp_earning_unit: e.target.value })}
            placeholder="z. B. Heißgetränk" maxLength={40} className="h-11 rounded-xl" />
          <p className="text-[12px] text-slate-500">Wofür erhalten Kunden einen Stempel? Unabhängig von der Belohnung.</p>
          <p className="text-[12px] font-semibold text-slate-600">Vorschau: <span className="text-slate-900">1 Stempel pro {(value.stamp_earning_unit || "").trim() || "Einkauf"}</span>{!(value.stamp_earning_unit || "").trim() && <span className="font-normal text-slate-400"> (Standard)</span>}</p>
        </Field>
      )}

      {(value.mode === "points_spend" || value.mode === "stamp_fixed") && (
        <Field label="Kundenanfrage sichtbar für">
          <select data-testid={`${idPrefix}-action-ttl`} value={value.loyalty_action_ttl_seconds || 120}
            onChange={(e) => set({ loyalty_action_ttl_seconds: parseInt(e.target.value, 10) })}
            className="h-11 w-full rounded-xl ring-1 ring-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-[var(--brand-primary)]">
            <option value={30}>30 Sekunden</option>
            <option value={60}>1 Minute</option>
            <option value={120}>2 Minuten</option>
            <option value={180}>3 Minuten</option>
            <option value={300}>5 Minuten</option>
          </select>
          <p className="text-[12px] text-slate-500">Bestimmt, wie lange eine Anfrage unter „Offene Kundenanfragen" angezeigt wird.</p>
        </Field>
      )}

      <Field label="Belohnungstext (für Kunden sichtbar)">
        <Input data-testid={`${idPrefix}-reward-title`} value={value.reward_title}
          onChange={(e) => set({ reward_title: e.target.value })}
          placeholder="z. B. 1 Gratis-Kaffee" className="h-11 rounded-xl" />
      </Field>

      {/* Branding */}
      <div>
        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Farbschema</label>
        <div className="flex flex-wrap gap-2 mt-2">
          {Object.entries(THEME_PRESETS).map(([key, p]) => (
            <button key={key} type="button" data-testid={`${idPrefix}-theme-${key}`} onClick={() => applyPreset(key)}
              className={`rounded-xl px-3 py-2 ring-1 press flex items-center gap-2 ${
                value.theme_preset === key ? "ring-[var(--bs-teal)] bg-teal-50/60" : "ring-slate-200 bg-white"
              }`}>
              <span className="flex -space-x-1">
                <span className="h-4 w-4 rounded-full ring-2 ring-white" style={{ background: p.primary }} />
                <span className="h-4 w-4 rounded-full ring-2 ring-white" style={{ background: p.secondary }} />
              </span>
              <span className="text-xs font-bold text-slate-600">{p.label}</span>
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <Field label="Primärfarbe">
            <div className="flex items-center gap-2">
              <input data-testid={`${idPrefix}-primary-color`} type="color" value={value.primary_color}
                onChange={(e) => set({ primary_color: e.target.value, theme_preset: "custom" })}
                className="h-11 w-14 rounded-xl border border-slate-200 bg-white p-1" />
              <span className="font-mono text-sm text-slate-500">{value.primary_color}</span>
            </div>
          </Field>
          <Field label="Sekundärfarbe">
            <div className="flex items-center gap-2">
              <input data-testid={`${idPrefix}-secondary-color`} type="color" value={value.secondary_color}
                onChange={(e) => set({ secondary_color: e.target.value, theme_preset: "custom" })}
                className="h-11 w-14 rounded-xl border border-slate-200 bg-white p-1" />
              <span className="font-mono text-sm text-slate-500">{value.secondary_color}</span>
            </div>
          </Field>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}>
    <label className="text-xs font-bold text-slate-500">{label}</label>
    {children}
  </div>
);

export const DEFAULT_CONFIG = {
  mode: "stamp_fixed",
  stamps_per_card: 10,
  spend_per_stamp: 10,
  carry_remainder: true,
  points_per_currency: 1,
  reward_threshold: 100,
  reward_title: "1 Gratis-Belohnung",
  currency: "€",
  stamp_earning_unit: "",
  loyalty_action_ttl_seconds: 120,
  primary_color: "#E11D48",
  secondary_color: "#F59E0B",
  theme_preset: "classic",
};

export function splitConfig(cfg) {
  return {
    branding: {
      primary_color: cfg.primary_color, secondary_color: cfg.secondary_color,
      theme_preset: cfg.theme_preset, logo_path: cfg.logo_path || null,
    },
    loyalty: {
      mode: cfg.mode, stamps_per_card: cfg.stamps_per_card, spend_per_stamp: cfg.spend_per_stamp,
      carry_remainder: cfg.carry_remainder, points_per_currency: cfg.points_per_currency,
      reward_threshold: cfg.reward_threshold, reward_title: cfg.reward_title, currency: cfg.currency,
      stamp_earning_unit: (cfg.stamp_earning_unit || "").trim() || null,
      loyalty_action_ttl_seconds: cfg.loyalty_action_ttl_seconds || 120,
    },
  };
}
